import {css, html, LitElement} from 'lit';
import {customElement, property, state} from 'lit/decorators';
import {FilterItem} from './filter-item';
import {normalizeStyles} from '@webs/components-library/theming';

@customElement('webs-filter')
export class Filter extends LitElement {

  static styles = [
    normalizeStyles,
    css`
      :host {
        --button-border-radius: 2.1rem;
        --button-font-family: var(--font-family-alternative);
        --button-font-weight: var(--font-weight-strong);
        --button-padding: .6rem 1.4rem;
      }

      a {
        color: var(--color-base);
        font-family: var(--font-family-base);
        font-size: .8rem;
        text-decoration: none;
      }

      .filter {
        padding-top: 2rem;
      }

      .filter__trigger {
        align-items: center;
        display: flex;
        font: var(--button-font-weight) .8rem/1.3 var(--button-font-family);
        justify-content: space-between;
        line-height: 1.2;
      }

      .filter__trigger.active svg {
        transform: scaleY(-1);
      }

      .filter__list {
        display: none;
        list-style: none;
        margin: 0;
        padding: 1.2rem .75rem 0;
        width: 100%;
      }

      .filter__list.open {
        display: block;
      }

      .filter__list li {
        margin: .6rem 0;
      }

      .filter__list li:first-child {
        margin-top: 0;
      }

      .filter__list li:last-child {
        margin-bottom: 0;
      }

      .filter__list a {
        align-items: baseline;
        display: flex;
      }

      .filter__list a::before {
        background: var(--color-white);
        border: 1px solid var(--color-grey);
        border-radius: 0.2rem;
        content: '';
        cursor: pointer;
        display: inline-block;
        height: 20px;
        min-width: 20px;
        margin-right: .6rem;
        transform: translateY(3px);
        width: 20px;
      }

      .filter__list a.checked::before {
        background: url("data:image/svg+xml,%3Csvg width='12' height='9' viewBox='0 0 12 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4.99372 9.00014L0.5 4.58139L1.5 3.50031L4.91959 6.87984L10.4762 0.835938L11.5 1.80805L4.99372 9.00014Z' fill='white'/%3E%3C/svg%3E%0A") center/11px 8px no-repeat, linear-gradient(216.4deg, var(--color-primary) 22.12%, var(--color-secondary) 110.96%);
        box-shadow: none;
        border: 0;
      }

      @media only screen and (min-width: 768px) {
        .filter {
          padding-top: 0;
          position: relative;
        }

        .filter__trigger {
          align-items: center;
          background: var(--color-light-grey);
          border-radius: var(--button-border-radius);
          color: var(--color-base);
          cursor: pointer;
          display: inline-flex;
          justify-content: center;
          outline: none;
          overflow: hidden;
          padding: var(--button-padding);
          position: relative;
          text-decoration: none;
          transition: background .1s linear, border .1s linear, color .1s linear;
          z-index: 1;
        }

        .filter__trigger svg {
          margin-left: .3rem;
        }

        .filter__trigger.active {
          background: var(--color-base);
          color: var(--color-white);
        }

        .filter__trigger.active svg {
          transform: scaleY(-1);
        }

        .filter__trigger.active svg path { fill: var(--color-white); }

        .filter__list {
          background: var(--color-white);
          border-radius: 8px;
          box-shadow: 8px 0px 50px rgba(24, 26, 35, 0.08);
          display: none;
          left: 0;
          list-style: none;
          margin: 0;
          min-width: 185px;
          max-width: 280px;
          padding: 1.2rem 1.6rem;
          position: absolute;
          top: calc(100% + .8rem);
          width: max-content;
          z-index: 2;
        }
      }
    `
  ]

  private isFilterClicked: boolean;
  private smallDevice: MediaQueryList = window.matchMedia('(min-width: 768px)');

  @state()
  private isDesktop: boolean;

  @property()
  name: string;

  @property()
  title: string;

  @property({type: Array})
  filters: FilterItem[];

  @property({attribute: 'active-filters', type: Array})
  activeFilters: string[] = [];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('resetfilterall', (event: CustomEvent) => {
      if (event.detail.noFilters) {
        this.activeFilters = [];
        this.toggleFilter();
      }
    })

    document.addEventListener('filterremove', (event: CustomEvent) => {
      if (!event.detail.filter || !event.detail.key) {
        return;
      }
      if (event.detail.key !== this.name) {
        return;
      }
      const currentFilterIndex = this.activeFilters.indexOf(event.detail.filter);
      if (currentFilterIndex > -1) {
        this.activeFilters.splice(currentFilterIndex, 1);
        this.toggleFilter(undefined, true);
      }
    })

    document.addEventListener('click', (event) => {
      if (!event.composedPath().includes(this)) {
        this.isFilterClicked = false;
        this.requestUpdate();
      }
    }, false);
  }

  protected render(): unknown {
    this.handleDeviceChange(this.smallDevice);

    return html`
      <div class="filter">
        <a
          class="filter__trigger ${this.name ? `filter__trigger--${this.name}` : ''} ${this.isFilterClicked ? 'active' : ''}"
          href="javascript:;" @click="${this.toggleSubmenu}">
          ${this.title}
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd"
                  d="M0.24001 1.88827C0.107456 1.75571 0.107456 1.5408 0.24001 1.40825L0.908247 0.74001C1.0408 0.607456 1.25571 0.607456 1.38827 0.74001L5.61173 4.96348C5.74429 5.09603 5.9592 5.09603 6.09175 4.96348L10.3152 0.74001C10.4478 0.607456 10.6627 0.607456 10.7952 0.74001L11.4635 1.40825C11.596 1.5408 11.596 1.75571 11.4635 1.88827L6.09175 7.25999C5.9592 7.39254 5.74429 7.39254 5.61173 7.25999L0.24001 1.88827Z"
                  fill="#181A23"/>
          </svg>
        </a>

        ${this.filters && this.filters.length > 0 ? html`
          <ul class="filter__list ${this.isFilterClicked ? 'open' : ''}">
            ${this.filters.map((item, index) => {
              let isChecked = false;
              const slug = item.slug ? item.slug : item.text;
              if (this.activeFilters && this.activeFilters.length > 0) {
                isChecked = this.activeFilters.indexOf(slug) != -1;
              }
              return html`
                <li>
                  <a href="${item.link ? item.link : 'javascript:;'}" @click="${this.toggleFilter}"
                     data-filter-slug="${slug}"
                     class="${isChecked ? 'checked' : ''}">${item.text}</a>
                </li>
              `;
            })}
          </ul>
        ` : ''}
      </div>
    `;
  }

  private toggleSubmenu(event: Event): void {
    this.isFilterClicked = !this.isFilterClicked;
    this.requestUpdate();
  }

  private toggleFilter(event?: Event, isRemove = false): void {
    let filterValue: string;

    if (event) {
      const target: HTMLElement = event.target as HTMLElement;
      filterValue = target.dataset.filterSlug;
      const currentFilterIndex = this.activeFilters.indexOf(filterValue);
      if (currentFilterIndex == -1) {
        // todo e anormal să folosești textul ca să filtrezi.
        this.activeFilters.push(filterValue);
      } else {
        this.activeFilters.splice(currentFilterIndex, 1);
      }
    }

    const addFilterEvent = new CustomEvent('filteradd', {
      detail: {key: this.name, value: filterValue, activeFilters: this.activeFilters, isRemove: isRemove},
      bubbles: true,
      composed: true
    });
    this.dispatchEvent(addFilterEvent);

    this.requestUpdate();
  }

  private handleDeviceChange(event): void {
    this.isDesktop = event.matches;
    this.requestUpdate();
  }
}
